import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, Lock, Tag, CreditCard as CreditCardIcon } from "lucide-react";
import { toast } from "sonner";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  PaymentRequestButtonElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { api } from "../../lib/api";
import { useBooking } from "../../lib/BookingContext";
import { isClinic } from "../../lib/site";
import { navigateExternal } from "../../lib/analytics";

const OPTIONS = {
  CLINIC: "clinic",
  ONLINE: "online",  // Online radio active, no method tile picked yet (Clinic layout)
  CARD: "card",
  PAYPAL: "paypal",
  WALLET: "wallet",  // Apple Pay / Google Pay (Clinic only, when device supports it)
};

// Detect which native wallet — if any — the visitor's device supports.
// Returns { key, label, node } where key ∈ "apple" | "google" | null.
function detectWallet() {
  if (typeof navigator === "undefined") return { key: null };
  // Preview override — lets the operator screenshot the Apple/Google Pay
  // tile from a desktop where Stripe's canMakePayment() returns null.
  // Adds a "mock" flag so the CTA renders a placeholder instead of
  // trying to fire the (unavailable) native sheet.
  try {
    const q = new URLSearchParams(window.location.search);
    const w = (q.get("wallet") || "").toLowerCase();
    if (w === "apple") return { key: "apple", label: "Apple Pay", mock: true };
    if (w === "google") return { key: "google", label: "Google Pay", mock: true };
  } catch (e) {
    // window unavailable during SSR — fall through to UA sniff below
  }
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return { key: "apple", label: "Apple Pay" };
  if (/Android/i.test(ua)) return { key: "google", label: "Google Pay" };
  if (/Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome/.test(ua)) {
    return { key: "apple", label: "Apple Pay" };
  }
  return { key: "google", label: "Google Pay" };
}

const elementStyle = {
  base: {
    // 16px minimum to prevent iOS Safari from zooming the page when a Card
    // field is tapped. Anything below 16px triggers the auto-zoom.
    fontSize: "16px",
    color: "#1A1A1A",
    fontFamily: 'Manrope, system-ui, sans-serif',
    "::placeholder": { color: "#9CA3AF" },
  },
  invalid: { color: "#D0021B" },
};

const cardIcons = (
  <>
    <span className="px-2 py-1 rounded bg-white border border-[#E5E5E5] text-[10px] font-bold tracking-wider" style={{ color: "#1A1F71" }}>VISA</span>
    <span className="px-2 py-1 rounded bg-white border border-[#E5E5E5] text-[10px] font-bold">
      <span className="text-[#EB001B]">●</span><span className="text-[#F79E1B] -ml-1">●</span>
    </span>
    <span className="px-2 py-1 rounded text-white text-[10px] font-bold tracking-wider" style={{ background: "#006FCF" }}>AMEX</span>
  </>
);

const paypalIcon = (
  <span className="px-2 py-1 rounded bg-white border border-[#E5E5E5] text-xs font-bold" style={{ fontFamily: "Manrope" }}>
    <span style={{ color: "#003087" }}>Pay</span><span style={{ color: "#009CDE" }}>Pal</span>
  </span>
);

function PaymentOption({ value, label, icons, savingsBadge, dataTestId, expanded, current, onSelect }) {
  const selected = current === value;
  return (
    <div
      className={`w-full rounded-lg border-2 transition-all ${
        selected ? "border-[#0466ff] bg-[#F0F6FF]" : "border-[#E5E5E5] bg-white hover:border-[#B3D1FF]"
      }`}
      data-testid={dataTestId}
    >
      <button
        type="button"
        onClick={() => onSelect(value)}
        className="w-full text-left px-4 py-3"
        data-testid={`${dataTestId}-radio`}
      >
        <div className="flex items-center gap-3">
          <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 grid place-items-center ${selected ? "border-[#0466ff]" : "border-[#9CA3AF]"}`}>
            {selected && <span className="w-2.5 h-2.5 rounded-full bg-[#0466ff]" />}
          </span>
          <span className="flex-1 text-sm sm:text-base font-semibold text-[#1A1A1A] leading-snug">{label}</span>
          {icons && <span className="hidden sm:flex flex-shrink-0 items-center gap-1.5">{icons}</span>}
        </div>
        {savingsBadge && (
          <div className="mt-2 ml-8" data-testid={`${dataTestId}-savings`}>{savingsBadge}</div>
        )}
        {icons && <div className="flex sm:hidden items-center gap-1.5 mt-3 ml-8">{icons}</div>}
      </button>
      {expanded && selected && (
        <div className="px-4 pb-4 ml-8">{expanded}</div>
      )}
    </div>
  );
}

export default function StepPayment() {
  const ctx = useBooking();
  const { service, location, slot, slotClinicianId, patient, back } = ctx;
  // Clinic's hybrid layout defaults to the pay-at-clinic radio being
  // active. Selecting the "Pay now" radio will auto-promote to Wallet if
  // the device supports Apple/Google Pay, otherwise defaults to ONLINE
  // (customer picks Card or PayPal tile). Dental keeps its historic
  // default of Pay-at-clinic pre-selected too.
  const [option, setOption] = useState(OPTIONS.CLINIC);
  const [submitting, setSubmitting] = useState(false);
  const [cardComplete, setCardComplete] = useState({ number: false, expiry: false, cvc: false });
  const [cardError, setCardError] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  // Sticky flag: once the user has manually picked a method tile, don't
  // auto-swap them to Wallet just because Stripe's canMakePayment
  // resolved a moment later. Prevents the tile jumping under their tap.
  const [userPickedMethod, setUserPickedMethod] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const wallet = useMemo(detectWallet, []);

  const base = service?.price || 0;
  const onlineTotal = base > 0 ? Math.round(base * 0.9) : 0;
  const cardReady = cardComplete.number && cardComplete.expiry && cardComplete.cvc;

  // Set up the Apple Pay / Google Pay Payment Request once Stripe is ready
  useEffect(() => {
    if (!stripe || base <= 0) return;
    try {
      const pr = stripe.paymentRequest({
        country: "IE",
        currency: "eur",
        total: { label: `${service?.name || "Booking"} — White Smile Dental`, amount: onlineTotal * 100 },
        requestPayerName: false,
        requestPayerEmail: false,
      });
      pr.canMakePayment().then((result) => {
        if (result) setPaymentRequest(pr);
      }).catch(() => {
        // Apple/Google Pay not available on this browser — ignore.
      });
    } catch (e) {
      // Stripe Payment Request constructor can throw on some browsers/iOS PWAs.
      // Falling back silently — the standard card form still works.
    }
  }, [stripe, base, onlineTotal, service?.name]);

  // Clinic-only: if the device has a native wallet available, auto-pick
  // it so the fast-payment CTA renders immediately when the customer
  // lands on Step 4. On devices without Apple/Google Pay we intentionally
  // leave `option` at ONLINE so the customer picks Card or PayPal
  // manually. Respects an explicit user pick so an async
  // canMakePayment() resolving late can't undo their tap.
  useEffect(() => {
    if (!isClinic() || userPickedMethod) return;
    if ((paymentRequest || wallet.mock) && option === OPTIONS.ONLINE) {
      setOption(OPTIONS.WALLET);
    }
     
  }, [paymentRequest]);

  // Wrap setOption for the clinic tiles so we remember when a tap came
  // from the user vs the auto-promote effect.
  const pickMethod = (v) => {
    setUserPickedMethod(true);
    setOption(v);
  };

  const createPendingBooking = async (paymentMethod) => {
    const fullPhone = patient.phone_country + patient.phone;
    const payOnline = paymentMethod !== null;
    const payload = {
      practice_id: location.id,
      service_id: service.id,
      clinician_id: slotClinicianId ? String(slotClinicianId) : null,
      appointment_time: slot,
      first_name: patient.first_name.trim(),
      last_name: patient.last_name.trim(),
      email: patient.email.trim() || `noemail+${Date.now()}@whitesmiledental.ie`,
      phone: fullPhone,
      notes: patient.notes || "",
      pay_online: payOnline,
      payment_method: paymentMethod,
      aerona_patient_id: patient.aerona_patient_id != null ? String(patient.aerona_patient_id) : null,
      force_new_patient: !!patient.force_new_patient,
      origin_url: window.location.origin,
    };
    const res = await api.createBooking(payload);
    sessionStorage.setItem("ws_last_booking", res.booking_id);
    return res;
  };

  const handleClinic = async () => {
    const res = await createPendingBooking(null);
    // Redirect directly to the per-site WP thank-you URL returned by the
    // backend. We used to hop through /booking/success first, but that
    // page relies on client-side isClinic() detection to pick the correct
    // fallback URL — and a full window.location.href navigation strips
    // the ?site=clinic query param, so clinic bookings were incorrectly
    // redirecting to the dental thank-you page. Using the server-signed
    // thank_you_url keeps site attribution authoritative.
    // navigateExternal() ensures GA4's linker param (_gl) is appended on
    // cross-domain redirects to whitesmiledental.ie so the thank-you
    // page keeps the same GA4 session + attribution.
    navigateExternal(res.thank_you_url || `/booking/success?booking_id=${res.booking_id}`);
  };

  const handlePayPal = async () => {
    const res = await createPendingBooking("paypal");
    if (res.payment_url) window.location.href = res.payment_url;
  };

  const finalizeStripe = async (paymentIntentId, bookingId) => {
    try { await api.stripeFinalizeIntent(paymentIntentId); } catch { /* webhook fallback */ }
    window.location.href = `/booking/success?stripe_intent=${paymentIntentId}&booking_id=${bookingId}`;
  };

  const handleCard = async () => {
    if (!stripe || !elements) throw new Error("Stripe not loaded yet. Please refresh.");
    const booking = await createPendingBooking("stripe");
    const intent = await api.stripeCreateIntent(booking.booking_id);
    if (intent.already_paid) {
      window.location.href = `/booking/success?booking_id=${booking.booking_id}`;
      return;
    }
    const cardNumber = elements.getElement(CardNumberElement);
    const result = await stripe.confirmCardPayment(intent.client_secret, {
      payment_method: {
        card: cardNumber,
        billing_details: {
          name: `${patient.first_name} ${patient.last_name}`.trim(),
          email: patient.email,
          phone: patient.phone_country + patient.phone,
        },
      },
    });
    if (result.error) throw new Error(result.error.message || "Card was declined.");
    if (result.paymentIntent?.status === "succeeded") {
      await finalizeStripe(result.paymentIntent.id, booking.booking_id);
    } else {
      throw new Error("Payment did not complete.");
    }
  };

  const submitOption = async (which) => {
    setSubmitting(true);
    setCardError(null);
    try {
      if (which === OPTIONS.CLINIC) await handleClinic();
      else if (which === OPTIONS.PAYPAL) await handlePayPal();
      else if (which === OPTIONS.CARD) await handleCard();
    } catch (e) {
      // Pydantic 422 returns `detail` as an array of `{type, loc, msg, input, url}`
      // objects. React won't render those directly — we must flatten to text first.
      const detail = e?.response?.data?.detail;
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        msg = detail
          .map((d) => {
            const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : d?.loc;
            const human = d?.msg || "Invalid value";
            return field ? `${field}: ${human}` : human;
          })
          .join(", ");
      } else {
        msg = e?.message || "Something went wrong. Please try again.";
      }
      if (e?.response?.status === 409) ctx.setConflict(msg);
      else if (which === OPTIONS.CARD) setCardError(msg);
      else toast.error(msg);
      setSubmitting(false);
    }
  };

  // Apple Pay / Google Pay handler — fires when the user authorizes the wallet
  useEffect(() => {
    if (!paymentRequest) return;
    const handler = async (ev) => {
      try {
        const booking = await createPendingBooking("stripe");
        const intent = await api.stripeCreateIntent(booking.booking_id);
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          intent.client_secret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false },
        );
        if (error) {
          ev.complete("fail");
          setCardError(error.message);
          return;
        }
        ev.complete("success");
        if (paymentIntent.status === "requires_action") {
          const { error: e2 } = await stripe.confirmCardPayment(intent.client_secret);
          if (e2) { setCardError(e2.message); return; }
        }
        await finalizeStripe(paymentIntent.id, booking.booking_id);
      } catch (e) {
        ev.complete("fail");
        setCardError(e?.message || "Wallet payment failed.");
      }
    };
    paymentRequest.on("paymentmethod", handler);
    return () => paymentRequest.off("paymentmethod", handler);
     
  }, [paymentRequest, stripe]);

  const inputBox = "rounded-md border-2 border-[#E5E5E5] bg-white px-3 py-2.5 focus-within:border-[#0466ff] transition-colors";

  const cardFields = useMemo(() => (
    <div className="space-y-3" data-testid="card-fields">
      {paymentRequest && (
        <>
          <div className="pt-1" data-testid="apple-pay-wrapper">
            <PaymentRequestButtonElement
              options={{ paymentRequest, style: { paymentRequestButton: { theme: "dark", height: "44px" } } }}
            />
          </div>
          <div className="flex items-center gap-3 text-xs text-[#9CA3AF] uppercase tracking-wider">
            <span className="flex-1 h-px bg-[#E5E5E5]" />
            <span>or pay by card</span>
            <span className="flex-1 h-px bg-[#E5E5E5]" />
          </div>
        </>
      )}
      <div>
        <div className="text-xs font-semibold uppercase text-[#5C5C5C] tracking-wide mb-1">Card number</div>
        <div className={inputBox} data-testid="card-number">
          <CardNumberElement
            options={{ style: elementStyle, showIcon: true, placeholder: "1234 1234 1234 1234" }}
            onChange={(e) => { setCardComplete((s) => ({ ...s, number: e.complete })); setCardError(e.error?.message || null); }}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-semibold uppercase text-[#5C5C5C] tracking-wide mb-1">Expiry</div>
          <div className={inputBox} data-testid="card-expiry">
            <CardExpiryElement
              options={{ style: elementStyle, placeholder: "MM / YY" }}
              onChange={(e) => { setCardComplete((s) => ({ ...s, expiry: e.complete })); setCardError(e.error?.message || null); }}
            />
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase text-[#5C5C5C] tracking-wide mb-1">CVC</div>
          <div className={inputBox} data-testid="card-cvc">
            <CardCvcElement
              options={{ style: elementStyle, placeholder: "CVC" }}
              onChange={(e) => { setCardComplete((s) => ({ ...s, cvc: e.complete })); setCardError(e.error?.message || null); }}
            />
          </div>
        </div>
      </div>
      {cardError && (
        <div className="text-xs text-[#D0021B] font-medium" data-testid="card-error">{cardError}</div>
      )}
      <button
        type="button"
        onClick={() => submitOption(OPTIONS.CARD)}
        disabled={submitting || !cardReady}
        className="ws-btn-primary w-full inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-[15px] sm:text-lg font-bold tracking-tight !px-3 !py-4 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="pay-card-submit"
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>
        ) : (
          <>Pay €{onlineTotal} - Confirm Booking <ArrowRight className="w-4 h-4" strokeWidth={3} /></>
        )}
      </button>
      <div className="text-[11px] text-[#5C5C5C] leading-tight">
        Your card details are sent directly to Stripe over a secure connection. We never see your card number.
      </div>
    </div>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [cardError, submitting, cardReady, paymentRequest, onlineTotal]);

  const paypalAction = (
    <button
      type="button"
      onClick={() => submitOption(OPTIONS.PAYPAL)}
      disabled={submitting}
      className="ws-btn-primary w-full inline-flex items-center justify-center gap-2 mt-3"
      data-testid="pay-paypal-submit"
    >
      {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>) : (<>Pay €{onlineTotal} with PayPal <ArrowRight className="w-4 h-4" strokeWidth={2.5} /></>)}
    </button>
  );

  // Prominent per-treatment savings badge shown under Card / PayPal radio
  // labels on the Clinic site — the old Bookly system had this and the
  // monetary framing lifts the pay-online conversion rate on the
  // higher-ticket whitening treatments (Save €20 lands harder than
  // "10% off").
  const savingsBadge = (
    <span
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#E6F8EE] text-[#0F8F4A] text-sm font-extrabold uppercase tracking-wide border-2 border-[#BDEACB]"
      style={{ fontFamily: "Manrope" }}
    >
      <Sparkles className="w-4 h-4" strokeWidth={2.5} />
      Save €{base - onlineTotal} by paying now — you only pay €{onlineTotal} total
    </span>
  );

  // ── Reusable clinic building blocks ────────────────────────────────────
  // Card form (no submit button — the black CTA below the tile grid
  // dispatches submitOption(CARD) instead, matching the mockup where the
  // CTA lives at the bottom of the sub-panel).
  const clinicCardFields = (
    <div className="space-y-3" data-testid="clinic-card-fields">
      <div>
        <div className="text-xs font-semibold uppercase text-[#5C5C5C] tracking-wide mb-1">Card number</div>
        <div className="rounded-md border-2 border-[#E5E5E5] bg-white px-3 py-2.5 focus-within:border-[#0466ff] transition-colors">
          <CardNumberElement
            options={{ style: elementStyle, showIcon: true, placeholder: "1234 1234 1234 1234" }}
            onChange={(e) => { setCardComplete((s) => ({ ...s, number: e.complete })); setCardError(e.error?.message || null); }}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-semibold uppercase text-[#5C5C5C] tracking-wide mb-1">Expiry</div>
          <div className="rounded-md border-2 border-[#E5E5E5] bg-white px-3 py-2.5 focus-within:border-[#0466ff] transition-colors">
            <CardExpiryElement
              options={{ style: elementStyle, placeholder: "MM / YY" }}
              onChange={(e) => { setCardComplete((s) => ({ ...s, expiry: e.complete })); setCardError(e.error?.message || null); }}
            />
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase text-[#5C5C5C] tracking-wide mb-1">CVC</div>
          <div className="rounded-md border-2 border-[#E5E5E5] bg-white px-3 py-2.5 focus-within:border-[#0466ff] transition-colors">
            <CardCvcElement
              options={{ style: elementStyle, placeholder: "CVC" }}
              onChange={(e) => { setCardComplete((s) => ({ ...s, cvc: e.complete })); setCardError(e.error?.message || null); }}
            />
          </div>
        </div>
      </div>
      {cardError && (
        <div className="text-xs text-[#D0021B] font-medium" data-testid="clinic-card-error">{cardError}</div>
      )}
    </div>
  );

  // Tile in the "Choose your payment method" strip (Clinic layout).
  const MethodTile = ({ value, testId, children }) => {
    const selected = option === value;
    return (
      <button
        type="button"
        onClick={() => pickMethod(value)}
        className={`rounded-md border-2 px-2 h-11 sm:h-12 flex items-center justify-center gap-1.5 transition-all ${
          selected ? "border-[#0466ff] bg-[#F0F6FF]" : "border-[#E5E5E5] bg-white hover:border-[#B3D1FF]"
        }`}
        data-testid={testId}
        aria-pressed={selected}
      >
        {children}
      </button>
    );
  };

  // Big contextual CTA rendered below the tile strip. Uses Stripe's
  // native PaymentRequestButton for wallet so Apple/Google's payment
  // sheet fires directly; falls back to standard buttons for Card and
  // PayPal. Renders nothing until a method tile is picked.
  const clinicSubmitCTA = () => {
    if (option === OPTIONS.ONLINE) {
      return (
        <div className="mt-3 text-center text-[11px] sm:text-xs text-[#5C5C5C] italic" data-testid="clinic-pick-method-hint">
          Choose a payment method above to continue
        </div>
      );
    }
    if (option === OPTIONS.WALLET) {
      if (paymentRequest) {
        // Native Apple Pay / Google Pay button — label locked to the
        // `book` preset so it renders "Book with Apple Pay" / "Book
        // with G Pay". A small grey caption below matches the "Fast,
        // secure payments" style above.
        return (
          <div className="mt-4" data-testid="clinic-wallet-cta">
            <PaymentRequestButtonElement
              options={{
                paymentRequest,
                style: {
                  paymentRequestButton: {
                    theme: "dark",
                    height: "44px",
                    type: "book",
                  },
                },
              }}
            />
            <div className="flex items-center justify-center gap-1 mt-2 text-[11px] text-[#5C5C5C]">
              Click to Confirm Booking <ArrowRight className="w-3 h-3" strokeWidth={2.5} />
            </div>
          </div>
        );
      }
      // Preview mock: this device can't fire the native sheet, but the
      // operator asked to see what the tile + CTA layout looks like.
      return (
        <button
          type="button"
          disabled
          className="mt-4 w-full flex flex-col items-center justify-center gap-0.5 rounded-lg py-3 text-white bg-[#0A0A0A] opacity-70 cursor-not-allowed"
          style={{ fontFamily: "Manrope" }}
          data-testid="clinic-wallet-cta-mock"
        >
          <span className="text-base sm:text-lg font-extrabold leading-tight">Pay €{onlineTotal} with {wallet.label} Now</span>
          <span className="text-xs sm:text-sm font-semibold opacity-80 inline-flex items-center gap-1">Confirm Booking <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} /></span>
        </button>
      );
    }
    if (option === OPTIONS.CARD) {
      return (
        <button
          type="button"
          onClick={() => submitOption(OPTIONS.CARD)}
          disabled={submitting || !cardReady}
          className="mt-4 w-full flex flex-col items-center justify-center gap-0.5 rounded-lg py-3 text-white bg-[#0A0A0A] hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ fontFamily: "Manrope" }}
          data-testid="clinic-pay-card-cta"
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2 text-base sm:text-lg font-extrabold"><Loader2 className="w-5 h-5 animate-spin" /> Processing…</span>
          ) : (
            <>
              <span className="text-base sm:text-lg font-extrabold leading-tight">Pay €{onlineTotal} with Card Now</span>
              <span className="text-xs sm:text-sm font-semibold opacity-80 inline-flex items-center gap-1">Confirm Booking <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} /></span>
            </>
          )}
        </button>
      );
    }
    if (option === OPTIONS.PAYPAL) {
      return (
        <button
          type="button"
          onClick={() => submitOption(OPTIONS.PAYPAL)}
          disabled={submitting}
          className="mt-4 w-full flex flex-col items-center justify-center gap-0.5 rounded-lg py-3 text-white bg-[#0A0A0A] hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ fontFamily: "Manrope" }}
          data-testid="clinic-pay-paypal-cta"
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2 text-base sm:text-lg font-extrabold"><Loader2 className="w-5 h-5 animate-spin" /> Redirecting…</span>
          ) : (
            <>
              <span className="text-base sm:text-lg font-extrabold leading-tight">Pay €{onlineTotal} with PayPal Now</span>
              <span className="text-xs sm:text-sm font-semibold opacity-80 inline-flex items-center gap-1">Confirm Booking <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} /></span>
            </>
          )}
        </button>
      );
    }
    return null;
  };

  const onlineSelected = option !== OPTIONS.CLINIC;
  const clinicSelected = option === OPTIONS.CLINIC;

  // ── Clinic render (hybrid layout) ──────────────────────────────────────
  if (isClinic()) {
    // Handler for selecting the "Pay now" outer radio. Promote to Wallet
    // if the native sheet is available so the customer gets a one-tap
    // path; otherwise land on ONLINE (tiles visible, no method chosen).
    const selectOnlineRadio = () => {
      setUserPickedMethod(true);
      setOption(paymentRequest || wallet.mock ? OPTIONS.WALLET : OPTIONS.ONLINE);
    };
    const selectClinicRadio = () => {
      setUserPickedMethod(true);
      setOption(OPTIONS.CLINIC);
    };
    return (
      // Bust out of the ws-card + BookingPage padding on mobile so the
      // radio cards go edge-to-edge (10 px viewport margins only).
      <div
        data-testid="step-payment"
        className="-mx-[26px] px-[10px] sm:mx-0 sm:px-0"
      >
        <h3
          className="text-lg sm:text-xl md:text-2xl font-extrabold text-[#0A1B4B] leading-tight mb-8 pl-[10px]"
          style={{ fontFamily: "Manrope" }}
          data-testid="clinic-payment-headline"
        >
          Please choose a payment option and tap Confirm:
        </h3>

        {/* Radio 1 — Pay at the clinic (DEFAULT) */}
        <div
          className={`rounded-lg border-2 transition-all mb-1 ${
            clinicSelected ? "border-[#0466ff] bg-[#F5F9FF]" : "border-[#E5E5E5] bg-white"
          }`}
          data-testid="clinic-pay-at-clinic-card"
        >
          <button
            type="button"
            onClick={selectClinicRadio}
            className="w-full text-left px-3 py-3 sm:px-4 sm:py-4 min-h-[104px] sm:min-h-[112px]"
            data-testid="clinic-pay-at-clinic-radio"
          >
            <div className="flex items-start gap-3">
              <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 grid place-items-center mt-0.5 ${clinicSelected ? "border-[#0466ff]" : "border-[#9CA3AF]"}`}>
                {clinicSelected && <span className="w-2.5 h-2.5 rounded-full bg-[#0466ff]" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-base sm:text-lg font-extrabold text-[#0A1B4B] leading-tight" style={{ fontFamily: "Manrope" }}>
                  Pay at the dental clinic — €{base}
                </div>
                <div className="mt-1 text-xs sm:text-sm text-[#1A1A1A]" data-testid="clinic-pay-later-subtitle">
                  I will pay at the dental clinic on the day of my appointment. I don't want 10% off.
                </div>
              </div>
            </div>
          </button>
        </div>

        {/* OR Pay Now To Get Our SPECIAL OFFER divider */}
        <div className="flex items-center justify-center gap-2 my-10 flex-wrap px-[10px]" data-testid="clinic-or-divider">
          <span className="uppercase tracking-wide text-[#5C5C5C] text-xs sm:text-sm">Or</span>
          <span className="text-sm sm:text-base font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope" }}>
            Pay Now To Get Our
          </span>
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-br from-[#FF3B3B] to-[#D0021B] text-white text-[11px] sm:text-xs font-extrabold uppercase tracking-wider shadow-sm"
            style={{ letterSpacing: "0.06em" }}
            data-testid="special-offer-badge"
          >
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
            Special offer
          </span>
        </div>

        {/* Radio 2 — Pay now (Extra 10% off) */}
        <div
          className={`rounded-lg border-2 transition-all ${
            onlineSelected ? "border-[#0466ff] bg-[#F5F9FF]" : "border-[#E5E5E5] bg-white"
          }`}
          data-testid="clinic-pay-online-card"
        >
          <button
            type="button"
            onClick={selectOnlineRadio}
            className="w-full text-left px-3 py-3 sm:px-4 sm:py-4 min-h-[104px] sm:min-h-[112px]"
            data-testid="clinic-pay-online-radio"
          >
            <div className="flex items-start gap-3">
              <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 grid place-items-center mt-0.5 ${onlineSelected ? "border-[#0466ff]" : "border-[#9CA3AF]"}`}>
                {onlineSelected && <span className="w-2.5 h-2.5 rounded-full bg-[#0466ff]" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-base sm:text-lg font-extrabold text-[#0A1B4B] leading-tight" style={{ fontFamily: "Manrope" }}>
                  Pay now for an extra 10% off — €{onlineTotal}
                </div>
                {base > 0 && (
                  <div className="mt-1 text-xs sm:text-sm flex items-center gap-2 flex-wrap" data-testid="clinic-online-savings">
                    <span>
                      <span className="line-through text-[#9CA3AF] mr-1">€{base}</span>
                      <span className="text-[#0F8F4A] font-extrabold">Save €{base - onlineTotal} (10% off)</span>
                    </span>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#E6F8EE] text-[#0F8F4A] text-[10px] font-extrabold uppercase tracking-wider border border-[#BDEACB]"
                      data-testid="clinic-online-offer-badge"
                    >
                      <Tag className="w-3 h-3" strokeWidth={2.5} />
                      Online offer
                    </span>
                  </div>
                )}
              </div>
            </div>
          </button>

          {onlineSelected && (
            <div className="px-3 pb-3 sm:px-4 sm:pb-4" data-testid="clinic-method-picker">
              <div className="text-sm font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "Manrope" }}>
                Choose your payment method
              </div>
              <div className={`grid gap-2 ${(paymentRequest || wallet.mock) ? "grid-cols-3" : "grid-cols-2"}`}>
                {(paymentRequest || wallet.mock) && (
                  <MethodTile value={OPTIONS.WALLET} testId="clinic-tile-wallet">
                    <span className="text-xs sm:text-sm font-extrabold text-[#1A1A1A] leading-tight text-center" style={{ fontFamily: "Manrope" }}>
                      {wallet.label}
                    </span>
                  </MethodTile>
                )}
                <MethodTile value={OPTIONS.CARD} testId="clinic-tile-card">
                  <CreditCardIcon className="w-4 h-4 text-[#5C5C5C] flex-shrink-0" strokeWidth={2} />
                  <span className="text-xs sm:text-sm font-bold text-[#1A1A1A] leading-tight">Card</span>
                </MethodTile>
                <MethodTile value={OPTIONS.PAYPAL} testId="clinic-tile-paypal">
                  <span className="text-xs sm:text-sm font-extrabold leading-tight" style={{ fontFamily: "Manrope" }}>
                    <span style={{ color: "#003087" }}>Pay</span><span style={{ color: "#009CDE" }}>Pal</span>
                  </span>
                </MethodTile>
              </div>

              <div className="flex items-center gap-1.5 mt-2 text-[11px] text-[#5C5C5C]">
                <Lock className="w-3 h-3" strokeWidth={2} />
                <span>Fast, secure payments. No extra fees.</span>
              </div>

              {option === OPTIONS.CARD && (
                <div className="mt-3">{clinicCardFields}</div>
              )}

              {clinicSubmitCTA()}
            </div>
          )}
        </div>

        {/* Bottom bar — small blue-outline BACK on the left; when the
            Pay-at-clinic radio is active, a full-width solid-blue
            CONFIRM appears on the right. Paid-online CTAs live inside
            Radio 2 itself. */}
        <hr className="border-[#E5E5E5] mt-10 mb-6" />
        <div className="flex items-center justify-between gap-3 pl-[10px] pr-[10px]">
          <button
            onClick={back}
            disabled={submitting}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-bold text-[#0466ff] bg-white border-2 border-[#0466ff] hover:bg-[#F0F6FF] transition-colors disabled:opacity-50 flex-shrink-0"
            data-testid="payment-back-button"
            style={{ fontFamily: "Manrope" }}
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> Back
          </button>
          {clinicSelected && (
            <button
              type="button"
              onClick={() => submitOption(OPTIONS.CLINIC)}
              disabled={submitting}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg py-3 text-base sm:text-lg font-extrabold uppercase tracking-wide text-white bg-[#0466ff] hover:bg-[#0355d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: "Manrope" }}
              data-testid="pay-clinic-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>
              ) : (
                <>CONFIRM <ArrowRight className="w-4 h-4" strokeWidth={2.5} /></>
              )}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Dental render (unchanged historic layout) ──────────────────────────
  return (
    <div data-testid="step-payment">
      <h3 className="text-base sm:text-lg font-extrabold mb-5 uppercase tracking-wide text-[#1A1A1A]" style={{ fontFamily: "Manrope" }}>
        Please choose a payment option:
      </h3>

      <div className="mb-6 space-y-2">
        <div className="flex items-center gap-2.5 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gradient-to-br from-[#FF3B3B] to-[#D0021B] text-white text-[11px] font-extrabold uppercase tracking-wider shadow-sm"
            style={{ letterSpacing: "0.06em" }}
            data-testid="special-offer-badge"
          >
            <Sparkles className="w-3 h-3" strokeWidth={2.5} />
            Special offer
          </span>
          <span className="text-sm sm:text-base text-[#1A1A1A] font-medium">
            Pay Now, Get an EXTRA 10% Off
          </span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2.5">
          <div className="text-sm sm:text-base text-[#1A1A1A] flex-shrink-0" data-testid="pay-online-total">
            You Only Pay <strong className="text-[#0466ff]">€{onlineTotal}</strong> Total
          </div>
          {base > 0 && (
            <span
              className="inline-flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-[#E6F8EE] text-[#0F8F4A] text-[9px] sm:text-[11px] font-bold uppercase tracking-wider border border-[#BDEACB] whitespace-nowrap"
              data-testid="discount-badge"
            >
              <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-[#0F8F4A]" />
              Save €{base - onlineTotal} by paying now
            </span>
          )}
        </div>
      </div>

      <div className="space-y-3 mb-8">
        <PaymentOption
          value={OPTIONS.CARD}
          label="I WILL PAY NOW BY CARD. I WANT AN EXTRA 10% OFF"
          icons={cardIcons}
          dataTestId="pay-online-card"
          expanded={cardFields}
          current={option}
          onSelect={setOption}
        />
        <PaymentOption
          value={OPTIONS.PAYPAL}
          label="I WILL PAY NOW BY PAYPAL. I WANT AN EXTRA 10% OFF"
          icons={paypalIcon}
          dataTestId="pay-paypal-card"
          current={option}
          onSelect={setOption}
          expanded={paypalAction}
        />
        <PaymentOption
          value={OPTIONS.CLINIC}
          label="I will pay at the dental clinic on the day. I don't want to pay now to get an extra 10% OFF"
          dataTestId="pay-at-clinic-card"
          current={option}
          onSelect={setOption}
        />
      </div>

      <hr className="border-[#E5E5E5] mb-6" />

      <div className="flex justify-between items-center gap-2 flex-nowrap">
        <button
          onClick={back}
          disabled={submitting}
          className="inline-flex items-center gap-1 rounded-lg px-2.5 sm:px-3 py-1.5 sm:py-2 text-[11px] sm:text-xs font-bold uppercase tracking-wide bg-white border-2 border-[#0466ff] text-[#0466ff] hover:bg-[#F0F6FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="payment-back-button"
          style={{ fontFamily: "Manrope" }}
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} /> Back
        </button>
        {option === OPTIONS.CLINIC && (
          <button
            type="button"
            onClick={() => submitOption(OPTIONS.CLINIC)}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-lg px-5 sm:px-7 py-3 sm:py-3.5 text-sm sm:text-base font-bold uppercase tracking-wide text-white bg-[#1FAD3A] hover:bg-[#17922E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            data-testid="pay-clinic-submit"
            style={{ fontFamily: "Manrope" }}
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>
            ) : (
              <>Confirm Booking <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5} /></>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
